/* ============================================================================
   assets/ll-brand-hero.js
   The behaviour for sections/ll-brand-hero.liquid.
   ============================================================================

   This used to be an inline <script> in the section. Inline meant it was part
   of the HTML document: re-rendered by Shopify and re-sent to the browser on
   every single page view, never cached, and counted against the time the
   server spends building the page. On the homepage the six hub sections came
   to 127 KB of the 1.8 MB document.

   The code is byte-identical on every render, so it belongs in an asset the
   browser can cache once and reuse across the whole site.

   WHAT CHANGED, AND WHAT DID NOT. The section used to bake its own id into the
   script as `var ROOT_ID = "{{ uid }}"`, which is the single reason this could
   not be a static file. One asset now serves every instance: it finds each
   root by its [data-llbh-root] marker and reads the id from
   data-section-id, which the markup already carried. Everything inside
   initInstance is the original code, unchanged.

   The per-instance JSON config block stays in the section, because that is
   real per-instance data rather than code.
============================================================================ */
(function () {
  /* Two instances of the same section put two identical <script src> tags on
     the page, and the browser runs both. */
  if (window.__llBrandHeroJS) return;
  window.__llBrandHeroJS = true;

  /* ---- block 1 of 2, extracted verbatim ---- */
  (function () {
    var inited = {};

    function initInstance(ROOT_ID) {
      function boot(root) {
          if (!root || root.dataset.llbhReady === '1') return;
          root.dataset.llbhReady = '1';

          var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          var fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
          var walls = Array.prototype.slice.call(root.querySelectorAll('[data-wall]'));
          var tiles = Array.prototype.slice.call(root.querySelectorAll('[data-tile]'));
          var observers = [];

          /* =====================================================================
             REVEAL — one shot, and only ever hides what it can also show
          ===================================================================== */
          if (!reduce && 'IntersectionObserver' in window) {
            walls.forEach(function (w) { w.classList.add('is-anim'); });
            var io = new IntersectionObserver(function (entries) {
              entries.forEach(function (en) {
                if (!en.isIntersecting) return;
                en.target.classList.add('is-in');
                io.unobserve(en.target);
              });
            }, { rootMargin: '0px 0px -6% 0px', threshold: 0.05 });
            tiles.forEach(function (t) { io.observe(t); });
            observers.push(io);
          }

          /* =====================================================================
             POINTER REACTION
             One listener for the whole section, batched into a single rAF. The
             getBoundingClientRect read happens inside the frame, never in the move
             handler, so moving the mouse never forces a synchronous layout.
          ===================================================================== */
          if (fine && !reduce) {
            var pending = null;
            var raf = 0;
            var lastTile = null;
            var lastMag = null;

            function schedule() { if (!raf) raf = requestAnimationFrame(flush); }

            function clearTile(t) {
              if (!t) return;
              t.style.removeProperty('--mx');
              t.style.removeProperty('--my');
              t.style.setProperty('--rx', '0deg');
              t.style.setProperty('--ry', '0deg');
            }
            function clearMag(m) { if (m) m.style.transform = ''; }

            function flush() {
              raf = 0;
              var p = pending;
              pending = null;
              if (!p) return;

              if (p.tile !== lastTile) { clearTile(lastTile); lastTile = p.tile; }
              if (p.mag !== lastMag) { clearMag(lastMag); lastMag = p.mag; }

              if (p.tile) {
                var r = p.tile.getBoundingClientRect();
                if (r.width && r.height) {
                  var px = (p.x - r.left) / r.width;
                  var py = (p.y - r.top) / r.height;
                  p.tile.style.setProperty('--mx', (px * 100).toFixed(1) + '%');
                  p.tile.style.setProperty('--my', (py * 100).toFixed(1) + '%');
                  /* Deliberately shallow — a hero that yaws about looks broken. */
                  p.tile.style.setProperty('--rx', ((0.5 - py) * 5).toFixed(2) + 'deg');
                  p.tile.style.setProperty('--ry', ((px - 0.5) * 7).toFixed(2) + 'deg');
                }
              }
              if (p.mag) {
                var mr = p.mag.getBoundingClientRect();
                if (mr.width && mr.height) {
                  var dx = (p.x - (mr.left + mr.width / 2)) / mr.width;
                  var dy = (p.y - (mr.top + mr.height / 2)) / mr.height;
                  p.mag.style.transform = 'translate(' + (dx * 7).toFixed(1) + 'px,' + (dy * 5).toFixed(1) + 'px)';
                }
              }
            }

            root.addEventListener('pointermove', function (e) {
              var tile = e.target.closest ? e.target.closest('[data-tile]') : null;
              var mag = e.target.closest ? e.target.closest('[data-magnet]') : null;
              if (!tile && !mag) {
                /* Only pay for a frame when there is something to undo. */
                if (lastTile || lastMag) { pending = { tile: null, mag: null, x: 0, y: 0 }; schedule(); }
                return;
              }
              pending = { tile: tile, mag: mag, x: e.clientX, y: e.clientY };
              schedule();
            }, { passive: true });

            root.addEventListener('pointerleave', function () {
              clearTile(lastTile); lastTile = null;
              clearMag(lastMag); lastMag = null;
            });

            /* A tile can be left by tabbing or scrolling too. */
            root.addEventListener('focusin', function (e) {
              var t = e.target.closest ? e.target.closest('[data-tile]') : null;
              if (t && t !== lastTile) { clearTile(lastTile); lastTile = null; }
            });

            root.llbhCleanup = function () { if (raf) cancelAnimationFrame(raf); };
          }

          root.llbhDestroy = function () {
            observers.forEach(function (o) { o.disconnect(); });
            if (root.llbhCleanup) root.llbhCleanup();
            root.dataset.llbhReady = '';
          };
        }

        function find() { return document.querySelector('[data-llbh-root][data-section-id="' + ROOT_ID + '"]'); }

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', function () { boot(find()); });
        } else { boot(find()); }
        document.addEventListener('shopify:section:load', function (e) {
          if (String(e.detail.sectionId) !== ROOT_ID) return;
          boot(find());
        });
        document.addEventListener('shopify:section:unload', function (e) {
          if (String(e.detail.sectionId) !== ROOT_ID) return;
          var el = find();
          if (el && el.llbhDestroy) el.llbhDestroy();
        });
    }

    function initOnce(id) {
      if (!id || inited[id]) return;
      inited[id] = true;
      initInstance(id);
    }

    function bootAll() {
      var roots = document.querySelectorAll("[data-llbh-root][data-section-id]");
      for (var i = 0; i < roots.length; i++) initOnce(roots[i].getAttribute("data-section-id"));
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", bootAll);
    } else {
      bootAll();
    }

    /* A section added in the theme editor arrives after first boot. Each
       instance registers its own load/unload handlers inside initInstance,
       so this only has to catch ids that have never been seen. */
    document.addEventListener("shopify:section:load", bootAll);
    document.addEventListener("shopify:section:unload", function (e) {
      var id = e && e.detail && String(e.detail.sectionId);
      if (id) delete inited[id];
    });
  })();

  /* ---- block 2 of 2, extracted verbatim ---- */
  (function () {
    var inited = {};

    function initInstance(ROOT_ID) {
      /* Slide affordance: an edge fade that follows real scroll position, plus a
           progress readout so "there is more" is unmistakable. Additive only - it
           reads scroll offsets and writes one attribute. */
        var SELS = [".llbh__wall"];
        var LABEL = "Swipe brands";
        var root = document.getElementById('shopify-section-' + ROOT_ID);
        if (!root) return;

        var mq = window.matchMedia('(max-width: 700px)');

        function railList() {
          var out = [];
          for (var i = 0; i < SELS.length; i++) {
            var found = root.querySelectorAll(SELS[i]);
            for (var j = 0; j < found.length; j++) out.push(found[j]);
          }
          return out;
        }

        function barFor(el) {
          var b = el.nextElementSibling;
          if (b && b.className === 'll-swipebar') return b;
          b = document.createElement('span');
          b.className = 'll-swipebar';
          b.setAttribute('aria-hidden', 'true');
          var lab = document.createElement('b');
          lab.textContent = LABEL;
          var track = document.createElement('u');
          track.appendChild(document.createElement('i'));
          b.appendChild(lab);
          b.appendChild(track);
          if (el.parentNode) el.parentNode.insertBefore(b, el.nextSibling);
          return b;
        }

        function sync(el) {
          var bar = barFor(el);
          var over = el.scrollWidth - el.clientWidth;
          if (!mq.matches || over <= 4) {
            el.removeAttribute('data-swipe');
            bar.hidden = true;
            return;
          }
          bar.hidden = false;
          var left = el.scrollLeft > 4;
          var right = el.scrollLeft < over - 4;
          el.setAttribute('data-swipe', left && right ? 'both' : (left ? 'left' : 'right'));

          var w = Math.max(18, (el.clientWidth / el.scrollWidth) * 100);
          var frac = el.scrollLeft / over;
          var thumb = bar.querySelector('i');
          thumb.style.width = w.toFixed(1) + '%';
          thumb.style.transform = 'translateX(' + (((100 - w) / w) * 100 * frac).toFixed(1) + '%)';
        }

        function boot() {
          railList().forEach(function (el) {
            if (el.__llSwipe) { sync(el); return; }
            el.__llSwipe = 1;
            var raf = 0;
            el.addEventListener('scroll', function () {
              if (raf) return;
              raf = requestAnimationFrame(function () { raf = 0; sync(el); });
            }, { passive: true });
            if (window.ResizeObserver) new ResizeObserver(function () { sync(el); }).observe(el);
            sync(el);
          });
        }

        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
        else boot();
        window.addEventListener('resize', boot);
        window.addEventListener('load', boot);
        document.addEventListener('shopify:section:load', boot);
    }

    function initOnce(id) {
      if (!id || inited[id]) return;
      inited[id] = true;
      initInstance(id);
    }

    function bootAll() {
      var roots = document.querySelectorAll("[data-llbh-root][data-section-id]");
      for (var i = 0; i < roots.length; i++) initOnce(roots[i].getAttribute("data-section-id"));
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", bootAll);
    } else {
      bootAll();
    }

    /* A section added in the theme editor arrives after first boot. Each
       instance registers its own load/unload handlers inside initInstance,
       so this only has to catch ids that have never been seen. */
    document.addEventListener("shopify:section:load", bootAll);
    document.addEventListener("shopify:section:unload", function (e) {
      var id = e && e.detail && String(e.detail.sectionId);
      if (id) delete inited[id];
    });
  })();
})();
